import React, { useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { Button } from '@efexteam/components';
import Autorenew from '@mui/icons-material/Autorenew';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { UploadStaStakeHolders } from '@efex-services/client/types';
import useHandleStakeholderRequest from '@pages/onboarding/hooks/useHandleStakeholderRequest';
import { aesDecryptCBC } from '@utils/crypt';
import {
  MexicoStakeHoldersFormData,
  StakeholderKYCStatus,
} from '@pages/onboarding/Mexico/types/stakeholders.type';
import PersonalInfo from './components/PersonalInfo';
import DocumentType, { DocumentTypeProps } from './components/DocumentType';
import DirectionInfo from './components/DirectionInfo';
import StakeHolderContext from '../../context/StakeHolderContext';
import LifeProof from './components/lifeProof';

interface StakeHoldersRefactorProps extends DocumentTypeProps {
  handleRemove: () => void;
  isLoading?: boolean;
  getStakeHolder: () => void;
  kycStatus: StakeholderKYCStatus;
}

const FormRefactor = ({
  defaultValues,
  handleRemove,
  getStakeHolder,
  isLoading,
  kycStatus,
  ...documentTypeProps
}: StakeHoldersRefactorProps) => {
  const { t } = useTranslation();

  const {
    deleteDocumensToDelete,
    getAllCurrentDocumentsIds,
    govermentIdDocuments,
  } = useContext(StakeHolderContext);

  const { executeRequest } = useHandleStakeholderRequest({
    country: 'USA',
    existingStakeholderId: defaultValues?.id,
    deleteDocumensToDelete,
    getAllCurrentDocumentsIds,
    govermentIdDocuments,
  });

  const methods = useForm({
    defaultValues: {
      ...defaultValues,
      ssn: defaultValues?.ssn ? aesDecryptCBC(defaultValues?.ssn) : '',
    },
    mode: 'onChange',
  });

  const handleStakeholderRequest = async (
    values: UploadStaStakeHolders | MexicoStakeHoldersFormData
  ) => executeRequest(values);

  const onSubmit = async (data: UploadStaStakeHolders) => {
    await executeRequest(data);

    getStakeHolder();
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit((data) => {
          onSubmit(data as unknown as UploadStaStakeHolders);
        })}
      >
        <PersonalInfo />
        <DocumentType defaultValues={defaultValues} {...documentTypeProps} />
        <DirectionInfo defaultValues={defaultValues} {...documentTypeProps} />
        <LifeProof
          kycStatus={kycStatus}
          handleStakeholderRequest={handleStakeholderRequest}
          getStakeHolder={getStakeHolder}
        />
        <Box className="flex justify-center gap-10">
          <Button
            size="large"
            type="button"
            className="justify-around"
            appearance="secondary"
            onClick={handleRemove}
            disabled={isLoading}
            endIcon={!isLoading ? <DeleteIcon /> : undefined}
            startIcon={
              !isLoading ? undefined : <Autorenew className="animate-spin" />
            }
          >
            {t('onbRemove')}
          </Button>

          <Button
            endIcon={
              isLoading ? <Autorenew className="animate-spin" /> : undefined
            }
            disabled={isLoading}
            size="large"
            type="submit"
            appearance="primary"
          >
            {t(defaultValues ? 'update' : 'onbSaveStk')}
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
};

export default FormRefactor;
